import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAccountant } from "@/contexts/AccountantContext";
import { getCurrencySymbol } from "@/lib/currencies";
import { todayInputDate } from "@/lib/formatDate";
import { useKipaToast } from "@/components/kipa";
import KipaIcon from "@/components/kipa/KipaIcon";
import { getQuickReplyDisplay } from "@/lib/quickReplyIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_LABELS = {
  carton: "Carton",
  box: "Box",
  roll: "Roll",
  pieces: "Pieces",
  pack: "Pack",
  bottle: "Bottle",
};

export default function SalesWizard({ open, onClose, onRecorded }) {
  const { fmt, currency } = useAccountant();
  const sym = getCurrencySymbol(currency);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [customer, setCustomer] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [accountType, setAccountType] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [saleDate, setSaleDate] = useState(() => todayInputDate());
  const [paymentDate, setPaymentDate] = useState(() => todayInputDate());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/inventory`).then((r) => setItems(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedId("");
      setItemName("");
      setQuantity("1");
      setCustomer("");
      setUnitPrice("");
      setPaymentStatus("unpaid");
      setAccountType("");
      setDepositAmount("");
      setSaleDate(todayInputDate());
      setPaymentDate(todayInputDate());
      setError("");
    }
  }, [open]);

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedId) || null,
    [items, selectedId]
  );

  const onSelectProduct = (id) => {
    setSelectedId(id);
    const item = items.find((i) => i.id === id);
    if (item) {
      setItemName(item.name);
      const sell = item.selling_price ?? item.unit_price ?? 0;
      if (sell > 0) setUnitPrice(String(sell));
    }
  };

  const qtyNum = parseFloat(quantity) || 0;
  const priceNum = parseFloat(unitPrice) || 0;
  const total = qtyNum * priceNum;
  const costPer = selectedItem?.cost_price || 0;
  const totalCost = costPer * qtyNum;
  const totalProfit = total - totalCost;
  const needsAccount = paymentStatus === "paid" || paymentStatus === "partial";

  const submit = async () => {
    const name = itemName.trim();
    if (!name) {
      setError("Select or type a product name.");
      return;
    }
    if (qtyNum <= 0) {
      setError("Enter a valid quantity.");
      return;
    }
    if (!customer.trim()) {
      setError("Enter the customer name.");
      return;
    }
    if (priceNum <= 0) {
      setError("Enter the selling price per unit.");
      return;
    }
    if (needsAccount && !accountType) {
      setError("Select Cash or Bank for this payment.");
      return;
    }
    if (paymentStatus === "partial") {
      const dep = parseFloat(depositAmount);
      if (!dep || dep <= 0) {
        setError("Enter the deposit amount received.");
        return;
      }
      if (dep > total) {
        setError("Deposit cannot be more than the sale total.");
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        inventory_item_id: selectedId || null,
        item_name: name,
        quantity: qtyNum,
        customer: customer.trim(),
        unit_price: priceNum,
        payment_status: paymentStatus,
        account_type: needsAccount ? accountType : null,
        amount_paid: paymentStatus === "partial" ? parseFloat(depositAmount) : null,
        sale_date: saleDate || undefined,
        payment_date: needsAccount ? paymentDate || undefined : undefined,
      };
      const res = await axios.post(`${API}/sales/record`, payload);
      const saleTotal = res.data?.transaction?.amount ?? total;
      showToast(`Sale recorded — ${fmt(saleTotal)}`, "success");
      onRecorded?.({ paymentStatus });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not record sale.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/45"
      onClick={onClose}
      data-testid="sales-wizard"
    >
      <div
        className="w-full max-w-[430px] bg-white rounded-t-3xl p-5 pb-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.25s ease-out" }}
      >
        <div className="w-12 h-1.5 bg-[var(--br2)] rounded-full mx-auto mb-4" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--ac)] mb-1">
          Record sale
        </div>
        <div className="text-[20px] font-black text-[var(--tx)] mb-1">Quick sale entry</div>
        <div className="text-[12px] text-[var(--tx2)] mb-4 leading-snug">
          Fill in product, customer, and payment — stock updates automatically.
        </div>

        <Field label="Sale date">
          <input
            type="date"
            className="s-inp"
            value={saleDate}
            onChange={(e) => setSaleDate(e.target.value)}
            data-testid="sale-date"
          />
        </Field>

        <Field label="Product">
          <select
            className="s-inp mb-2"
            value={selectedId}
            onChange={(e) => onSelectProduct(e.target.value)}
            data-testid="sale-product-select"
          >
            <option value="">— Select from inventory —</option>
            {items.map((it) => (
              <option key={it.id} value={it.id}>
                {it.name} ({Number(it.stock_qty)} {UNIT_LABELS[it.unit_type] || it.unit_type || "units"} left)
              </option>
            ))}
          </select>
          <input
            className="s-inp"
            placeholder="Or type product name"
            value={itemName}
            onChange={(e) => {
              setItemName(e.target.value);
              setSelectedId("");
            }}
            data-testid="sale-product-name"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantity">
            <input
              type="number"
              min="0"
              step="any"
              className="s-inp"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="sale-quantity"
            />
          </Field>
          <Field label={`Price per unit (${sym})`}>
            <input
              type="number"
              min="0"
              step="any"
              className="s-inp"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              data-testid="sale-unit-price"
            />
          </Field>
        </div>

        <Field label="Customer name">
          <input
            className="s-inp"
            placeholder="e.g. John"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            data-testid="sale-customer"
          />
        </Field>

        <Field label="Has the customer paid?">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "paid" },
              { id: "partial", label: "Deposit only" },
              { id: "unpaid" },
            ].map((opt) => {
              const { icon: ChipIcon, label } = getQuickReplyDisplay(opt);
              return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  setPaymentStatus(opt.id);
                  if (opt.id === "unpaid") setAccountType("");
                }}
                data-testid={`sale-payment-${opt.id}`}
                className={`chip inline-flex items-center gap-1.5 ${paymentStatus === opt.id ? "chip-active" : ""}`}
              >
                {ChipIcon && (
                  <KipaIcon
                    icon={ChipIcon}
                    size="sm"
                    color={paymentStatus === opt.id ? "#fff" : "var(--color-primary)"}
                  />
                )}
                {label}
              </button>
            );
            })}
          </div>
        </Field>

        {paymentStatus === "partial" && (
          <Field label={`Deposit amount received (${sym})`}>
            <input
              type="number"
              min="0"
              step="any"
              className="s-inp"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder={total > 0 ? `Max ${total}` : "Amount received now"}
              data-testid="sale-deposit"
            />
          </Field>
        )}

        {needsAccount && (
          <>
            <Field label="Payment date">
              <input
                type="date"
                className="s-inp"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="sale-payment-date"
              />
            </Field>
            <Field label="Payment received into">
            <div className="flex gap-2">
              {[
                { id: "cash" },
                { id: "bank" },
              ].map((opt) => {
                const { icon: ChipIcon, label } = getQuickReplyDisplay(opt);
                return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAccountType(opt.id)}
                  data-testid={`sale-account-${opt.id}`}
                  className={`chip flex-1 inline-flex items-center justify-center gap-1.5 ${accountType === opt.id ? "chip-active" : ""}`}
                >
                  {ChipIcon && (
                    <KipaIcon
                      icon={ChipIcon}
                      size="sm"
                      color={accountType === opt.id ? "#fff" : "var(--color-primary)"}
                    />
                  )}
                  {label}
                </button>
              );
              })}
            </div>
          </Field>
          </>
        )}

        {paymentStatus === "unpaid" && (
          <div className="text-[11px] text-[var(--tx3)] mb-3 leading-snug">
            You can mark payment later under <b>People</b> → customer → <b>Mark paid</b>, or from the Dashboard.
          </div>
        )}

        {total > 0 && (
          <div className="bg-[var(--al)] border border-[var(--ad)] rounded-2xl p-3 mb-3 text-[12px]">
            <div className="flex justify-between mb-1">
              <span className="text-[var(--tx2)]">Sale total</span>
              <span className="font-extrabold text-[var(--tx)]">{fmt(total)}</span>
            </div>
            {costPer > 0 && (
              <>
                <div className="flex justify-between mb-1">
                  <span className="text-[var(--tx2)]">Cost ({qtyNum} × {fmt(costPer)})</span>
                  <span className="font-bold text-[var(--tx2)]">{fmt(totalCost)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[var(--ad)]">
                  <span className="text-[var(--gn)] font-bold">Profit</span>
                  <span className="font-extrabold text-[var(--gn)]">{fmt(totalProfit)}</span>
                </div>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="text-[12px] text-[var(--rd)] font-semibold mb-2">{error}</div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="sale-submit"
          className="w-full bg-[var(--ac)] text-white rounded-2xl py-3.5 font-extrabold text-[14px] mb-2 disabled:opacity-60"
        >
          {saving ? "Recording…" : "Record sale"}
        </button>
        <button type="button" onClick={onClose} className="w-full text-[var(--tx3)] py-2 text-[12px]">
          Cancel
        </button>

        <style>{`
          .s-inp {
            background: var(--sf2);
            border: 1.5px solid var(--br);
            border-radius: 12px;
            padding: 10px 14px;
            color: var(--tx);
            font-size: 15px;
            width: 100%;
            font-family: inherit;
            outline: none;
          }
          .s-inp:focus { border-color: var(--ac); }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}
